import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import {
  editEmployeeAssignments,
  fetchAbsenceTypeBalances,
  fetchEmployee,
  fetchEmployeeAbsencePlan,
  fetchEmployeeAbsences,
  fetchEmployeeBalances,
  fetchEmployeeGroups,
  fetchEmployeePatterns,
  fetchEmployeeRecordsCalendars,
  fetchEmployeesRecords,
  fetchOrganizationUnits,
  modifyEmployeeBalances,
  uploadMedia,
} from "src/Pages/Company/Absences/api";
import {
  IEmployeeAssignments,
  IAbsenceTypeBalances,
  IEmployeeBalances,
  IEmployeePatternAssignments,
  IEmployeeRecordCalendars,
  IEmployeeRecords,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";
import {
  IAbsencePlan,
  IEmployeeAbsences,
  IEmployeeDetails,
  Status,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import {
  IEmployeeGroup,
  IOrganizationUnit,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { IParsedBalancesFormData } from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsModifyBalance/AbsenceRecordsModifyBalanceForm";

export const useFetchEmployeesRecords = (params: {
  companyId: string;
  currentYear: string;
  page: number;
  size: number;
  sort?: string[] | undefined;
  filters?: { field: string; value: string | string[] }[];
}) =>
  useQuery<IEmployeeRecords, Error>({
    queryKey: ["employees-records", params],
    queryFn: async ({ signal }) =>
      fetchEmployeesRecords({
        ...params,
        signal,
      }),
    placeholderData: (prev) => prev,
  });

export const useFetchEmployeeRecordsCalendars = (
  companyId: string,
  employeeId: string,
  years?: string[]
) =>
  useQuery<IEmployeeRecordCalendars, Error>({
    queryKey: ["employee-records", { companyId, employeeId, years }],
    queryFn: async ({ signal }) =>
      fetchEmployeeRecordsCalendars({ companyId, employeeId, years, signal }),
  });

export const useFetchEmployeePatterns = (employeeId: string) =>
  useQuery<IEmployeePatternAssignments, Error>({
    queryKey: ["patterns", { employeeId }],
    queryFn: async ({ signal }) =>
      fetchEmployeePatterns({ employeeId, signal }),
    initialData: { patternAssignments: [] },
  });

export const useSetAssingmentsForEmployee = (employeeId: string) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (formData: IEmployeeAssignments) => {
      const result = await editEmployeeAssignments(employeeId, formData);

      return result;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["employee-records", { employeeId }],
        }),
        queryClient.invalidateQueries({
          queryKey: ["patterns", { employeeId }],
        }),
      ]);
    },
  });

  return mutation;
};

export const useFetchEmployeeBalances = (employeeId?: string) =>
  useQuery<IEmployeeBalances, Error>({
    queryKey: ["employee-balances", { employeeId }],
    queryFn: ({ signal }) => fetchEmployeeBalances(employeeId!, { signal }),
    enabled: !!employeeId,
  });

export const useFetchAbsenceTypeBalance = (
  employeeId: string,
  billingCycles: string[],
  absenceTypeId?: string
) =>
  useQuery<IAbsenceTypeBalances, Error>({
    queryKey: [
      "absence-type-balances",
      { employeeId, absenceTypeId },
      billingCycles,
    ],
    enabled: !!absenceTypeId && !isEmpty(billingCycles),
    queryFn: ({ signal }) =>
      fetchAbsenceTypeBalances({
        employeeId,
        absenceTypeId,
        billingCycles,
        signal,
      }),
  });

export const useModifyEmployeeBalances = (employeeId: string) =>
  useMutation({
    mutationFn: async (formData: IParsedBalancesFormData) => {
      const result = await modifyEmployeeBalances(employeeId, formData);

      return result;
    },
  });

export const useFetchEmployee = (employeeId?: string) =>
  useQuery<IEmployeeDetails, Error>({
    queryKey: ["employee", { employeeId }],
    queryFn: () => fetchEmployee(employeeId!),
    enabled: !!employeeId,
  });

export const useFetchEmployeeAbsences = ({
  employeeId,
  from,
  to,
  statuses,
}: {
  employeeId: string;
  from: string;
  to: string;
  statuses: Status[];
}) =>
  useQuery<IEmployeeAbsences, Error>({
    queryKey: ["employee-absences", { employeeId, from, to, statuses }],
    queryFn: () => fetchEmployeeAbsences({ employeeId, from, to, statuses }),
    enabled: !!employeeId,
  });

export const useUploadAttachment = () =>
  useMutation({
    mutationFn: async (file: File) => {
      const response = await uploadMedia(file);

      return response;
    },
    onSuccess: () => {
      dynamicNotification(__("Plik został pomyślnie przesłany!"), "success");
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd podczas przesyłania pliku."),
        "error",
        "top-right",
        7000
      );
    },
  });

export const useFetchEmployeeGroups = (companyId: string) =>
  useQuery<IEmployeeGroup[], Error>({
    queryKey: ["employee-groups", { companyId }],
    queryFn: () => fetchEmployeeGroups(companyId),
  });

export const useFetchOrganizationUnits = (companyId: string) =>
  useQuery<IOrganizationUnit[], Error>({
    queryKey: ["organization-units", { companyId }],
    queryFn: () => fetchOrganizationUnits(companyId),
  });

export const useFetchAbsencePlan = (
  employeeId: string,
  year: string,
  enabled: boolean
) =>
  useQuery<IAbsencePlan, Error>({
    enabled: enabled && !!employeeId,
    queryKey: ["employee-absence-plan", { employeeId, year }],
    queryFn: ({ signal }) =>
      fetchEmployeeAbsencePlan({ employeeId, year, signal }),
    retry: 0,
  });
